import { TopicInfo } from '../../topic-list-view/TopicListItemView.sync'
import { AnswerHit } from '../../../services/algoliaService'

export interface EventHit {
  id: number
  public_id?: number
  private_id?: number
  content_type: 'event'
  objectID: string
  title: string
  description: string
  starts_at: number
  ends_at: number
  source: 'community'
  type?: string
  image?: string
  location?: string
  topic_url: string
  url?: string
  username: string
  _highlightResult?: object
}

export interface Hit {
  id: number
  private_id?: number
  public_id?: number
  objectID: string
  topic_url: string
  url: string
  title: string
  forum: number
  forum_title: string
  source: CommunitySourceType
  content_type: TopicContentType
  category_id: number
  category_title: string
  has_answer: boolean
  article_label: string
  username: string
  _highlightResult?: object
}

export interface ZendeskHit {
  objectID: string
  title: string
  url: string
  topic_url?: string
  source: 'zendesk'
  content: string
  _highlightResult: string
}

export interface SalesforceHit {
  objectID: string
  title: string
  url: string
  topic_url?: string
  source: 'salesforce'
  content: string
  _highlightResult: string
}

export interface SkilljarHit {
  objectID: string
  title: string
  url: string
  topic_url?: string
  source: 'skilljar'
  content: string
  _highlightResult: string
}

export interface FreshdeskHit {
  objectID: string
  title: string
  url: string
  topic_url?: string
  source: 'freshdesk'
  content: string
  _highlightResult: string
}

export interface FederatedHit {
  source: 'federated'
  objectID: string
  title: string
  url: string
  content: string
  tags?: string[]
  topic_url: undefined
  _highlightResult: string
}

export function isCommunityResult (el: SearchResult): el is Hit | EventHit {
  return !isZendeskResult(el) && !isSalesforceResult(el) && !isSkilljarResult(el) && !isFreshdeskResult(el) && !isFederatedResult(el)
}

export function isCommunityResultEvent (el: EventHit | Hit | TopicInfo): el is EventHit {
  return el.content_type === 'event'
}

export function isZendeskResult (el: SearchResult): el is ZendeskHit {
  return el.source === 'zendesk'
}

export function isSalesforceResult (el: SearchResult): el is SalesforceHit {
  return el.source === 'salesforce'
}

export function isSkilljarResult (el: SearchResult): el is SkilljarHit {
  return el.source === 'skilljar'
}

export function isFreshdeskResult (el: SearchResult): el is FreshdeskHit {
  return el.source === 'freshdesk'
}

export function isFederatedResult (el: SearchResult): el is FederatedHit {
  return el.source === 'federated'
}

export type SearchType = 'zendesk' | 'community' | 'salesforce' | 'skilljar' | 'freshdesk' | 'federated'

export type DoSearch = (input: string) => void

export type GetItemPropsFn = (data: {item: SearchResultWithMetadata, index: number}) => {}

export type GetItemPropsNoAnalyticsFn = (data: {item: SearchResult, index: number, disabled: boolean}) => {}

export function isMultiIndexResult (results: SearchResults | AllSearchResults | null): results is AllSearchResults {
  return results !== null && !('index' in results)
}

export interface SearchResults {
  hitsPerPage: number
  page: number
  queryID: string
  index: string
  nbHits: number
  hits: Array<EventHit|Hit|ZendeskHit|SalesforceHit|SkilljarHit|FreshdeskHit|FederatedHit>
  nbPages: number
  query: string
}

export interface AllSearchResults {
  [index: string]: SearchResults | undefined
}

export interface TopicClickedEvent {
  type: string
  topic_url: string
  click_source: string
  topic_position: number
  topic_title: string
  topic_topic_type: string
  category_id?: number
  category_title?: string
}

export interface TopicClickedGoogleAnalyticsEvent {
  eventCategory: 'Live Search'
  eventAction: 'Topic Clicked'
  eventLabel: {
    Link: string
    Position: number
    Type: SearchType | TopicContentType
  }
}

export type SearchResult = EventHit | Hit | ZendeskHit | SalesforceHit | SkilljarHit | FreshdeskHit | AnswerHit | FederatedHit

export type SearchResultWithMetadata = SearchResult & {
  queryID: string
  indexName: string
  position: number
}
